import { getLicenseType } from '@shared/constants/organization/LicenseUtils';
import { AllLicenseTypes } from '@shared/models/organizations/AllLicenseTypes';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlertsStore } from './state/AlertsContainer';
import { useOrganizationStore } from './state/OrganizationContainer';
import useResolveCurrentOrg from './useResolveCurrentOrg';

export default function useCurrentOrgDetailsFetching() {
    const { t } = useTranslation();
    const { organizationId, managedOrgId } = useResolveCurrentOrg();

    const currentOrg = useOrganizationStore((state) => state.currentOrg);
    const memberships = useOrganizationStore((state) => state.memberships);
    const isLoading = useOrganizationStore((state) => state.loadingData.currentOrg);

    const fetchOrgDetailData = useCallback(async () => {
        const { currentOrg, loadingData } = useOrganizationStore.getState();
        if (!organizationId || currentOrg?.id == organizationId || loadingData.currentOrg) {
            return;
        }
        try {
            useOrganizationStore.getState().handleLoadingDataChange('currentOrg', true);
            const result = await useOrganizationStore.getState().loadCurrentOrganizationDetails(organizationId);
            if (result.resultType != 'Ok') {
                throw new Error('Error fetching organization data');
            }
        } catch (error) {
            useAlertsStore.getState().showError(t('common:organization.error'));
        } finally {
            useOrganizationStore.getState().handleLoadingDataChange('currentOrg', false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    useEffect(() => {
        fetchOrgDetailData();
    }, [fetchOrgDetailData]);

    const remainingSeats = useMemo(() => {
        if (!currentOrg || currentOrg.currentMemberCount == null || !currentOrg.maxMembers) return 0;
        if (currentOrg.maxMembers < currentOrg.currentMemberCount) return 0;
        return currentOrg.maxMembers - currentOrg.currentMemberCount;
    }, [currentOrg]);

    const isExpiredOrg = useMemo(() => {
        if (!currentOrg || !currentOrg.licenseExpirationDate) return false;
        return currentOrg.licenseExpirationDate && new Date(currentOrg.licenseExpirationDate) < new Date();
    }, [currentOrg]);

    const licenseType: AllLicenseTypes = useMemo(() => {
        return getLicenseType(currentOrg);
    }, [currentOrg]);

    const currentMembership = useMemo(() => {
        if (!memberships || !organizationId) return null;
        return useOrganizationStore.getState().getOrganizationMembership(organizationId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, memberships]);

    const originatingOrgMembership = useMemo(() => {
        if (!memberships || !currentOrg || !currentOrg.originatedByOrganizationId) return null;
        return useOrganizationStore.getState().getOrganizationMembership(currentOrg.originatedByOrganizationId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentOrg, memberships]);

    const membership = useMemo(() => {
        if (!organizationId) return null;
        if (currentMembership) return currentMembership;
        if (originatingOrgMembership) {
            const newMembership = {
                ...originatingOrgMembership,
            };
            newMembership.organizationId = organizationId;
            newMembership.isAdmin = true;
            return newMembership;
        }
        return null;
    }, [currentMembership, originatingOrgMembership, organizationId]);

    const hasSomeMemberEditPermissions = useMemo(() => {
        return !!(
            currentMembership?.isAdmin ||
            currentMembership?.canRemoveMembers ||
            currentMembership?.canChangeMemberPermissions ||
            originatingOrgMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations
        );
    }, [currentMembership, originatingOrgMembership]);

    const canManageBilling = useMemo(() => {
        return !!(
            currentMembership?.canManageBilling ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    //     canAccessOrganizationProfiles: boolean;
    // canEditOrganizationProfiles: boolean;
    const canAccessOrganizationProfiles = useMemo(() => {
        return !!(
            currentMembership?.canAccessOrganizationProfiles ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canEditOrganizationProfiles = useMemo(() => {
        return !!(
            currentMembership?.canEditOrganizationProfiles ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canShareSkillsWithOrganization = useMemo(() => {
        return !!(
            currentMembership?.canShareSkillsWithOrganization ||
            currentMembership?.canManageOrganizationSkills ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canShareMcpServersWithOrganization = useMemo(() => {
        return !!(
            currentMembership?.canShareMcpServersWithOrganization ||
            currentMembership?.canManageOrganizationMcpServers ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canManageOrganizationSkills = useMemo(() => {
        return !!(
            currentMembership?.canManageOrganizationSkills ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canManageOrganizationMcpServers = useMemo(() => {
        return !!(
            currentMembership?.canManageOrganizationMcpServers ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canUseExternalSkills = useMemo(() => {
        return !!(
            currentMembership?.canUseExternalSkills ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canUseExternalMcpServers = useMemo(() => {
        return !!(
            currentMembership?.canUseExternalMcpServers ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canInviteMembers = useMemo(() => {
        return !!(
            currentMembership?.canInviteMembers ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canViewMembers = useMemo(() => {
        return !!(
            currentMembership?.canViewMembers ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canCreateAuthClients = useMemo(() => {
        return !!(
            currentMembership?.canCreateAuthClients ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canViewAuthClients = useMemo(() => {
        return !!(
            currentMembership?.canViewAuthClients ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canManageChildOrganizations = useMemo(() => {
        return !!(
            currentMembership?.canManageChildOrganizations ||
            currentMembership?.isAdmin ||
            originatingOrgMembership?.canManageChildOrganizations ||
            originatingOrgMembership?.isAdmin
        );
    }, [currentMembership, originatingOrgMembership]);

    const canManageGovernance = useMemo(() => {
        return (
            !!currentOrg?.isEmployeeAccess &&
            !!(
                currentMembership?.isAdmin ||
                currentMembership?.canManageGlobalAutomationRules ||
                originatingOrgMembership?.isAdmin ||
                originatingOrgMembership?.canManageGlobalAutomationRules
            )
        );
    }, [currentOrg, currentMembership, originatingOrgMembership]);

    const canViewOrgDetails = useMemo(() => {
        return !!(managedOrgId || (currentOrg?.maxMembers ?? 0) > 1);
    }, [currentOrg, managedOrgId]);

    const isManagedOrg = useMemo(() => {
        return !!(managedOrgId || currentOrg?.originatedByOrganizationId);
    }, [managedOrgId, currentOrg]);

    /** Membership is owned by the customer's identity provider, so the portal cannot invite members. */
    const isScimManaged = useMemo(() => {
        return !!currentOrg?.isScimEnabled;
    }, [currentOrg]);

    /** Cephable sends this organization no email at all. Set by Cephable staff; read-only here. */
    const areCephableEmailsBlocked = useMemo(() => {
        return !!currentOrg?.disableCephableEmails;
    }, [currentOrg]);

    return {
        isLoading,
        currentOrg,
        membership,
        remainingSeats,
        isExpiredOrg,
        licenseType,
        hasSomeMemberEditPermissions,
        canManageBilling,
        canCreateAuthClients,
        canViewAuthClients,
        canInviteMembers,
        canViewMembers,
        canManageChildOrganizations,
        canManageGovernance,
        canViewOrgDetails,
        canAccessOrganizationProfiles,
        canEditOrganizationProfiles,
        canShareSkillsWithOrganization,
        canShareMcpServersWithOrganization,
        canManageOrganizationSkills,
        canManageOrganizationMcpServers,
        canUseExternalSkills,
        canUseExternalMcpServers,
        isManagedOrg,
        isScimManaged,
        areCephableEmailsBlocked,
    };
}
